import * as Yup from "yup";
import { ERROR_MESSAGES, PASSWORD_REGEX } from "constants/validation";

/**
 * Password Schema
 * Represents password validation rules for changing password
 */
export const PasswordSchema = Yup.object().shape({
  currentPassword: Yup.string().required(ERROR_MESSAGES.CURRENT_PASSWORD_REQUIRED),
  newPassword: Yup.string()
    .required(ERROR_MESSAGES.NEW_PASSWORD_REQUIRED)
    .min(8, ERROR_MESSAGES.MIN_CHARS(8))
    .matches(PASSWORD_REGEX, ERROR_MESSAGES.PASSWORD_COMPLEXITY)
    .notOneOf(
      [Yup.ref("currentPassword")],
      ERROR_MESSAGES.NEW_PASSWORD_SAME_AS_OLD
    ),
  confirmPassword: Yup.string()
    .required(ERROR_MESSAGES.CONFIRM_PASSWORD_REQUIRED)
    .oneOf([Yup.ref("newPassword")], ERROR_MESSAGES.PASSWORDS_MUST_MATCH),
})

/**
 * Reusable schema for setting a new password (e.g. creating/resetting)
 * Used in Admin and Profile forms
 */
export const SetPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .required(ERROR_MESSAGES.PASSWORD_REQUIRED)
    .min(8, ERROR_MESSAGES.MIN_CHARS(8))
    .matches(PASSWORD_REGEX, ERROR_MESSAGES.PASSWORD_COMPLEXITY),
  confirmPassword: Yup.string()
    .required(ERROR_MESSAGES.CONFIRM_PASSWORD_REQUIRED)
    .oneOf([Yup.ref("password")], ERROR_MESSAGES.PASSWORDS_MUST_MATCH),
});

/**
 * Dynamic schema for User Account information
 * Used in Profile/user/form.js
 * @param {boolean} isEditMode - If true, validates oldPassword. If false, validates email.
 */
export const getUserAccountSchema = (isEditMode) =>
  Yup.object().shape({
    ...(isEditMode
      ? {
          oldPassword: Yup.string()
            .required(ERROR_MESSAGES.CURRENT_PASSWORD_REQUIRED)
            .min(8, ERROR_MESSAGES.MIN_CHARS(8))
            .matches(PASSWORD_REGEX, ERROR_MESSAGES.PASSWORD_COMPLEXITY),
        }
      : {
          email: Yup.string()
            .email(ERROR_MESSAGES.INVALID_EMAIL)
            .required(ERROR_MESSAGES.REQUIRED),
        }),
    password: Yup.string()
      .required(ERROR_MESSAGES.PASSWORD_REQUIRED)
      .min(8, ERROR_MESSAGES.MIN_CHARS(8))
      .matches(PASSWORD_REGEX, ERROR_MESSAGES.PASSWORD_COMPLEXITY)
      .test("not-weak", ERROR_MESSAGES.PASSWORD_WEAK, (value) => {
        const weakPasswords = [
          "password",
          "12345678",
          "123456789",
          "admin123",
          "password123",
          "qwerty",
          "11111111",
        ];
        return !weakPasswords.includes(value ? value.toLowerCase() : "");
      }),
    confirmPassword: Yup.string()
      .required(ERROR_MESSAGES.CONFIRM_PASSWORD_REQUIRED)
      .oneOf([Yup.ref("password"), null], ERROR_MESSAGES.PASSWORDS_MUST_MATCH),
  });
